// PersonalityTraits.jsx
import React, { useMemo, useState, useEffect } from "react";
import styles from "./PersonalityTraits.module.css";
import { useTranslation } from "react-i18next";

export default function PersonalityTraits({ payload }) {
  const { i18n, t } = useTranslation("user");
  const defaultTraits = [50, 50, 50];
  const temperament =
    payload?.personalityTraits && payload.personalityTraits.length >= 3
      ? payload.personalityTraits.slice(0, 3).map((v) => Number(v) || 0)
      : defaultTraits;

  const chars = payload?.characterTraits || {
    discipline: 50,
    mentalOpenness: 50,
    emotionalOpenness: 50,
    perspective: 50,
  };

  // descriptions + long (for modal)
  const CHAR_DESCRIPTIONS = {
    discipline: {
      short: t("profile.personalityCharDisShort"),
      long: t("profile.personalityCharDisLong"),
    },
    mentalOpenness: {
      short: t("profile.personalityCharMenShort"),
      long: t("profile.personalityCharMenLong"),
    },
    emotionalOpenness: {
      short: t("profile.personalityCharEmoShort"),
      long: t("profile.personalityCharEmoLong"),
    },
    perspective: {
      short: t("profile.personalityCharPerShort"),
      long: t("profile.personalityCharPerLong"),
    },
  };

  const TEMP_LABELS = [
    t("profile.personalityTempEmo"),
    t("profile.personalityTempAct"),
    t("profile.personalityTempSoc"),
  ];
  const tempColors = ["#FB7185", "#60A5FA", "#34D399"]; // customize por clã

  const tempSlices = temperament.map((val, idx) => ({
    label: TEMP_LABELS[idx],
    value: Math.round(val),
    color: tempColors[idx],
  }));

  const charEntries = [
    {
      key: "discipline",
      label: t("profile.personalityCharDis"),
      value: Number(chars.discipline || 0),
    },
    {
      key: "mentalOpenness",
      label: "Abertura Mental",
      value: Number(chars.mentalOpenness || 0),
    },
    {
      key: "emotionalOpenness",
      label: "Abertura Emocional",
      value: Number(chars.emotionalOpenness || 0),
    },
    {
      key: "perspective",
      label: "Perspectiva",
      value: Number(chars.perspective || 0),
    },
  ];

  // tooltip state (hover)
  const [hovered, setHovered] = useState(null);
  // modal state (open with fuller text)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFor, setModalFor] = useState(null);

  // animate bar fills on mount
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.left}>
        <h4 className={styles.sectionTitle}>{t("profile.tempTitle")}</h4>

        <div
          className={styles.donutWrap}
          role="img"
          aria-label="Gráfico de temperamento"
        >
          <svg viewBox="0 0 120 120" className={styles.donutSVG}>
            <circle cx="60" cy="60" r="40" fill="#fff" />

            {tempSlices.map((s, i) => {
              const radius = 40 - i * 10;
              const circumference = 2 * Math.PI * radius;
              const dash = (s.value / 100) * circumference;
              const gap = Math.max(0, circumference - dash);
              return (
                <g
                  key={s.label}
                  transform="rotate(-90 60 60)"
                  className={styles.sliceGroup}
                >
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke="#eee"
                    strokeWidth="8"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="8"
                    strokeDasharray={
                      mounted ? `${dash} ${gap}` : `0 ${circumference}`
                    }
                    strokeLinecap="round"
                    className={styles.slice}
                    aria-hidden
                  />
                </g>
              );
            })}

            {/* <text
              x="60"
              y="56"
              textAnchor="middle"
              className={styles.centerValue}
            >
              {tempSlices[0].value}%
            </text>
            <text
              x="60"
              y="74"
              textAnchor="middle"
              className={styles.centerLabel}
            >
              {tempSlices[0].label}
            </text> */}
          </svg>
        </div>

        <div className={styles.legend}>
          {tempSlices.map((s) => (
            <div key={s.label} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ background: s.color }}
              />
              <div className={styles.legendText}>
                <div className={styles.legendLabel}>{s.label}</div>
                <div className={styles.legendPercent}>{s.value}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.right}>
        <h4 className={styles.sectionTitle}>{t("profile.charTitle")}</h4>

        <div className={styles.charList}>
          {charEntries.map((c) => (
            <div key={c.key} className={styles.charRow}>
              <div className={styles.charInfo}>
                <div className={styles.charLabelRow}>
                  <div className={styles.charLabel}>{c.label}</div>
                  {/* <button
                    className={styles.infoBtn}
                    aria-label={`Mais sobre ${c.label}`}
                    onClick={() => openModalFor(c.key)}
                  >
                    <span className={styles.infoIcon} aria-hidden>
                      ℹ️
                    </span>
                  </button> */}
                </div>

                <div className={styles.charDesc}>
                  {CHAR_DESCRIPTIONS[c.key].short}
                </div>
              </div>

              <div className={styles.barWrap}>
                <div
                  className={styles.barBg}
                  // onMouseEnter={() => setHovered(c.key)}
                  // onMouseLeave={() => setHovered(null)}
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={c.value}
                >
                  <div
                    className={styles.barFill}
                    style={{ width: mounted ? `${c.value}%` : `0%` }}
                  />
                </div>
                <div className={styles.charValue}>{c.value}%</div>

                {/* simple tooltip */}
                {hovered === c.key && (
                  <div className={styles.tooltip} role="tooltip">
                    <strong>{c.label}</strong>
                    <div style={{ fontSize: 13, marginTop: 6 }}>
                      {CHAR_DESCRIPTIONS[c.key].short}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal (explanatory) */}
      {modalOpen && modalFor && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <header className={styles.modalHeader}>
              <h3>{charEntries.find((x) => x.key === modalFor)?.label}</h3>
              <button
                className={styles.modalClose}
                aria-label="Fechar"
                onClick={() => setModalOpen(false)}
              >
                X
              </button>
            </header>
            <div className={styles.modalBody}>
              <p style={{ marginTop: 8 }}>{CHAR_DESCRIPTIONS[modalFor].long}</p>

              {/* example micro-exercises tailor-made */}
              <div style={{ marginTop: 12 }}>
                <strong>Exercício rápido</strong>
                <p style={{ margin: 6, color: "#374151" }}>
                  {modalFor === "discipline"
                    ? "Escolha 1 micro-tarefa de 2 minutos e repita por 7 dias no mesmo horário."
                    : modalFor === "mentalOpenness"
                    ? "Liste 3 explicações alternativas para um problema hoje."
                    : modalFor === "emotionalOpenness"
                    ? "Nomeie uma emoção agora e fique 60s observando sem agir."
                    : "Observe pensamentos como eventos por 2 minutos; descreva sem se identificar."}
                </p>
              </div>

              <div
                style={{
                  marginTop: 14,
                  borderTop: "1px dashed #e6e9ef",
                  paddingTop: 10,
                  color: "#6b7280",
                }}
              >
                <small>Fonte: processos da ACT adaptados ao seu perfil.</small>
              </div>
            </div>

            <footer className={styles.modalFooter}>
              <button
                className="btn-outline"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Fechar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
